import type { PropsWithChildren } from 'react';
import React, { Children, useRef } from 'react';
import type { SwiperOptions } from 'swiper';
import { Navigation, Scrollbar } from 'swiper';
import { SwiperSlide } from 'swiper/react';
import type { ScrollbarOptions } from 'swiper/types';

import type { Maybe } from '@/lib/types';
import { getBreakpointSize } from '@/theme';

import * as S from './ProductSlider.styles';

export interface ProductSliderProps {
  buttonOffset?: number;
  children: React.ReactNode;
  disableGutters?: boolean;
  isMobile?: boolean;
  minHeight?: string;
  variantColor?: 'light' | 'dark';
}

export const ProductSlider = ({
  buttonOffset,
  children,
  disableGutters,
  isMobile,
  minHeight = '372px',
  variantColor = 'light',
}: ProductSliderProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  return (
    <S.Wrapper
      ref={wrapperRef}
      buttonOffset={buttonOffset}
      data-test-id='ProductSliderWrapper'
      data-testid='ProductSliderWrapper'
      disableGutters={disableGutters}
      isMobile={isMobile}
      minHeight={minHeight}
      variantColor={variantColor}
    >
      {children}
    </S.Wrapper>
  );
};

const Header = ({ children }: PropsWithChildren) => {
  return <S.Title data-test-id='ProductSliderTitle'>{children}</S.Title>;
};

export interface SlideProps {
  breakpoints?: Maybe<SwiperOptions['breakpoints']>;
  children: React.ReactNode;
  hideNavigation?: boolean;
  onSlideChange?: (activeIndex: number) => void;
  scrollbar?: ScrollbarOptions | boolean;
  separator?: boolean;
  slidesPerView?: Maybe<number>;
  slidesPerViewForMobile?: number | 'auto';
  spaceBetween?: number;
}

const Slider = ({
  breakpoints,
  children,
  hideNavigation = false,
  onSlideChange,
  scrollbar,
  separator = true,
  slidesPerView: initialSlidesPerView,
  slidesPerViewForMobile = 2.45,
  spaceBetween = 20,
}: SlideProps) => {
  const slidesPerView = initialSlidesPerView || 4;
  const itemsCount = Children.count(children);
  const defaultBreakpoints = {
    [getBreakpointSize('md')]: {
      slidesPerView: slidesPerView,
      spaceBetween: spaceBetween,
    },
    [getBreakpointSize('lg')]: {
      slidesPerView: slidesPerView,
      spaceBetween: spaceBetween,
    },
  };

  return (
    <S.StyledSwiper
      $hideNavigation={hideNavigation || slidesPerView >= itemsCount}
      $itemsCount={itemsCount}
      $slidesPerView={slidesPerView}
      $slidesPerViewForMobile={slidesPerViewForMobile}
      breakpoints={breakpoints || defaultBreakpoints}
      centerInsufficientSlides
      centeredSlidesBounds
      data-test-id='ProductSliderStyledSwiper'
      modules={[Navigation, Scrollbar]}
      navigation
      onSlideChange={(swiper) =>
        onSlideChange && onSlideChange(swiper.activeIndex)
      }
      scrollbar={scrollbar}
      separator={separator ? 'true' : 'false'}
      slidesPerView={slidesPerViewForMobile}
      spaceBetween={spaceBetween}
      watchSlidesProgress
    >
      {Children.map(
        children,
        (child, index) => (
          // prettier-ignore
          <SwiperSlide key={index} data-test-id={`ProductSliderSwiperSlide-${index}`}>{child}</SwiperSlide>
        ), //NOSONAR
      )}
    </S.StyledSwiper>
  );
};

ProductSlider.Header = Header;
ProductSlider.Slider = Slider;

ProductSlider.displayName = 'ProductSlider';
